import { FilterCollection } from '@infinity/ca-tester/helpers/filter'
import Model from '@infinity/shared/models/base'
import { Api, JsonData } from '@infinity/shared/helpers/api'
import { AuthUtil } from '@infinity/shared/utils/auth'
import axios from 'axios'

export const DefaultLimit = 25

export type ApiParams = { [k: string]: string | number | string[] | number[] | null }

export enum SortDirection { Asc = 'asc', Desc = 'desc' }

export default class Repository {
  protected api: Api
  protected apiRoute: string
  protected Model: { new(): Model }
  protected getter: string | null
  protected setter: string | null
  protected additionalParams: ApiParams = {}
  protected sorts: string[] = []

  constructor (api: Api, apiRoute: string, model: { new(): Model }, getter: string | null = null, setter: string | null = null) {
    this.api = api
    this.apiRoute = apiRoute
    this.Model = model
    this.getter = getter
    this.setter = setter
  }

  async fetch (filterCollection: FilterCollection | null = null, limit = DefaultLimit, offset = 0): Promise<Model[]> {
    const data = []

    const params: { filters?: string[]; [k: string]: string | number | string[] | undefined } = {
      limit,
      offset,
      ...this.additionalParams
    }

    if (filterCollection) {
      const existingFilterParams: string[] = params.filters || []
      params.filters = [
        ...existingFilterParams,
        ...filterCollection.toApi()
      ]
    }

    if (this.sorts) {
      params.sorts = this.sorts
    }

    const response = await axios.get(`${this.api}/${this.apiRoute}`, {
      ...{
        params
      },
      ...this.requestConfig
    })

    if (response && response.data) {
      let items = response.data

      if (this.getter !== null) {
        items = items[this.getter]
      }

      if (response.data && items) {
        for (const item of items) {
          data.push(
            new this.Model().fromApiTransformer(item, this.api)
          )
        }
      }

      this.resetParams()
    }

    return data
  }

  async find (id: string | number | null = null, path: string | null = null): Promise<Model | null> {
    const response = await axios.get(`${this.api}/${this.apiRoute}${id ? `/${id}` : ''}${path ? `/${path}` : ''}`, {
      ...{
        params: this.additionalParams
      },
      ...this.requestConfig
    })

    let data = response.data

    if (this.getter !== null) {
      data = data[this.getter]
    }

    if (!data) {
      return null
    }

    this.resetParams()

    return new this.Model().fromApiTransformer(data, this.api)
  }

  async create (model: Model, getter: string | null = null): Promise<JsonData | null> {
    let data: JsonData | { [k: string]: JsonData | undefined } | undefined = model.toApiTransformer(this.api)

    if (this.setter !== null) {
      data = {
        [this.setter]: data
      }
    }

    const response = await axios.post(`${this.api}/${this.apiRoute}`, data, {
      params: this.additionalParams,
      ...this.requestConfig
    })

    if (!response.data) {
      return null
    }

    this.resetParams()

    if (getter) {
      return response.data[getter]
    }

    return response.data
  }

  async postBulk (models: Model[], getter: string | null = null): Promise<JsonData | null> {
    const data = []

    for (const model of models) {
      data.push(model.toApiTransformer(this.api))
    }

    const response = await axios.post(`${this.api}/${this.apiRoute}`, data, {
      params: this.additionalParams,
      ...this.requestConfig
    })

    if (!response.data) {
      return null
    }

    this.resetParams()

    if (getter) {
      return response.data[getter]
    }

    return response.data
  }

  withParams (params: ApiParams) {
    this.additionalParams = params

    return this
  }

  sortBy (fieldName: string, direction: SortDirection) {
    this.sorts.push(`${fieldName}-${direction}`)

    return this
  }

  protected get requestConfig (): { headers: { [k: string]: string | null } } | null {
    if (!AuthUtil.authenticatedHeaders) {
      return null
    }

    if (this.api === Api.Hub) {
      return { headers: { 'x-auth-token': AuthUtil.authenticatedHeaders['x-auth-token'] } }
    }

    return { headers: { Authorization: AuthUtil.authenticatedHeaders.Authorization } }
  }

  private resetParams () {
    this.sorts = []
    this.additionalParams = {}
  }
}
